import { create } from 'zustand';
import {
  readServerSessionConfig,
  readTiaProjectRef,
  sameTiaProjectIdentity,
  sameTiaProjectRef,
  type ChatSessionConfigSnapshot,
  type PlcModelSource,
  type TiaProjectRef,
} from '@realplc/types';

export interface SessionTiaTargetState {
  /** The conversation described: the open one, or null for a new one before its first turn. */
  sessionId: string | null;
  /** The TIA project the conversation validates in (E2): the user's choice, else what its snapshot holds. */
  tiaProjectRef?: TiaProjectRef;
  /** The user chose `tiaProjectRef`, and the server has not stored it yet. */
  tiaProjectRefDirty: boolean;
  /** The CPU model the server resolved for the conversation (E9), with `plcModelSource`; both or neither. */
  plcModel?: string;
  plcModelSource?: PlcModelSource;
  /**
   * The project picker popped up for this conversation's choice among several
   * open projects (E7 ②), and no target has settled since, so it does not pop
   * up again. Cleared when another conversation opens; kept when a new
   * conversation's first turn names it, since that is the same conversation.
   */
  projectChoicePrompted: boolean;

  /**
   * Takes the open conversation's snapshot, whenever the conversation or its
   * snapshot changes (AppSecure). What the snapshot holds is what the server
   * stored, so it never marks the reference dirty; a choice the user has not
   * sent yet outlives a new snapshot of the same conversation, and nothing else.
   */
  syncSessionTiaTarget: (sessionId: string | null, snapshot: ChatSessionConfigSnapshot | undefined) => void;
  /** The project picker's choice (T5): 「新建工程」 is `{ kind: 'new' }` (E11 ④). Marks it dirty. */
  setSessionTiaProjectRef: (ref: TiaProjectRef) => void;
  /**
   * Clears the conversation's reference, so E7's default resolves again: the
   * way out when a reference blocks an Agent from before P2, which can use
   * none (E1; T5 review). Marks it dirty, and the next turn sends the clear.
   */
  clearSessionTiaProjectRef: () => void;
  /**
   * The choice the next turn of `sessionId` carries, only while it is dirty
   * and only for that conversation: the reference, or null for a clear.
   */
  unsentTiaProjectRef: (sessionId: string | null) => TiaProjectRef | null | undefined;
  /** A new conversation's first turn gave it `sessionId`: what was chosen for it before is now that session's. */
  adoptDraftSession: (sessionId: string) => void;
  /** The server stored the open conversation under another ID (AppSecure's followServerSession): what was chosen for it goes with it. */
  renameSession: (from: string, to: string) => void;
  /**
   * The server stored `stored` as the session's reference. The user's choice
   * is no longer unsent when it names that project (sameTiaProjectIdentity:
   * a server may store other names for it), and takes the stored names; one
   * made since, or one the server did not take, stays dirty and goes with the
   * next turn.
   */
  confirmTiaProjectRefStored: (sessionId: string, stored: TiaProjectRef | undefined) => void;
  /** The picker popped up for the choice (true), or a target settled and it may again (false). */
  setProjectChoicePrompted: (prompted: boolean) => void;
}

/**
 * The open conversation's TIA target, in one place (TIA 根治 P2, T2b), so that
 * the composer's TIA button, the Agent settings dialog, the post-generation card
 * and the project picker resolve the same target (readTiaReadiness), and the
 * next turn sends what they showed.
 *
 * The reference goes to the server only in the turn after the user changed it:
 * a turn that carries none keeps the stored one, and the server writes its own
 * (a project a validation job created, T8). So what arrives from the server,
 * through the snapshot, is never dirty: sent back, it would at best repeat the
 * server, and at worst put `new` back over the project a validation just
 * created (E11). utils/sessionTiaTarget.ts drives a turn through this store.
 */
export const useSessionTiaTargetStore = create<SessionTiaTargetState>((set, get) => ({
  sessionId: null,
  tiaProjectRefDirty: false,
  projectChoicePrompted: false,

  syncSessionTiaTarget: (sessionId, snapshot) => set((state) => {
    const sameConversation = state.sessionId === sessionId;
    const keepChoice = sameConversation && state.tiaProjectRefDirty;
    const stored = readServerSessionConfig(snapshot);
    return {
      sessionId,
      // The same object when nothing moved, so its readers do not re-render.
      tiaProjectRef: keepChoice || sameTiaProjectRef(stored.tiaProjectRef, state.tiaProjectRef)
        ? state.tiaProjectRef
        : stored.tiaProjectRef,
      tiaProjectRefDirty: keepChoice,
      plcModel: stored.plcModel,
      plcModelSource: stored.plcModelSource,
      projectChoicePrompted: sameConversation && state.projectChoicePrompted,
    };
  }),

  setSessionTiaProjectRef: (ref) => {
    const chosen = readTiaProjectRef(ref);
    if (!chosen) return;
    set({ tiaProjectRef: chosen, tiaProjectRefDirty: true });
  },

  clearSessionTiaProjectRef: () => set({ tiaProjectRef: undefined, tiaProjectRefDirty: true }),

  unsentTiaProjectRef: (sessionId) => {
    const { sessionId: current, tiaProjectRef, tiaProjectRefDirty } = get();
    if (current !== sessionId || !tiaProjectRefDirty) return undefined;
    return tiaProjectRef ?? null;
  },

  adoptDraftSession: (sessionId) => set((state) => (state.sessionId === null ? { sessionId } : state)),

  renameSession: (from, to) => set((state) => (state.sessionId === from ? { sessionId: to } : state)),

  confirmTiaProjectRefStored: (sessionId, stored) => set((state) => {
    if (state.sessionId !== sessionId || !state.tiaProjectRefDirty) return state;
    if (!sameTiaProjectIdentity(state.tiaProjectRef, stored)) return state;
    return {
      tiaProjectRefDirty: false,
      tiaProjectRef: sameTiaProjectRef(state.tiaProjectRef, stored) ? state.tiaProjectRef : stored,
    };
  }),

  setProjectChoicePrompted: (prompted) => set((state) => (
    state.projectChoicePrompted === prompted ? state : { projectChoicePrompted: prompted }
  )),
}));
